import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import './Dashboard.css';

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter(order => 
        order.order_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.phone.includes(searchTerm)
      );
      setFilteredOrders(filtered);
    }
  }, [searchTerm, orders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://gokulnair.com/new_project/api/get-orders');
      setOrders(response.data);
      setFilteredOrders(response.data);
    } catch (err) {
      setError('Failed to fetch orders');
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentTypeText = (type) => {
    switch (type) {
      case 0: return 'Zelle';
      case 1: return 'Cash App';
      default: return 'Unknown';
    }
  };

  const getPaymentStatusText = (status) => {
    switch (status) {
      case 0: return 'Pending';
      case 1: return 'Confirmed by User';
      case 2: return 'Confirmed by Admin';
      case 3: return 'Rejected by Admin';
      case 4: return 'Rejected by User';
      default: return 'Unknown';
    }
  };

  const getPaymentStatusClass = (status) => {
    switch (status) {
      case 0: return 'status-pending';
      case 1: return 'status-confirmed-user';
      case 2: return 'status-confirmed-admin';
      case 3: return 'status-rejected-admin';
      case 4: return 'status-rejected-user';
      default: return 'status-pending';
    }
  };

  const handleStatusUpdate = async (orderId, newStatus, sku = '') => {
    // If status is "Confirmed by Admin" (value 2), show confirmation modal
    if (newStatus === 2) {
      setPendingStatusUpdate({ orderId, newStatus, sku });
      setShowConfirmModal(true);
      return;
    }

    await updatePaymentStatus(orderId, newStatus, sku);
  };

  const updatePaymentStatus = async (orderId, newStatus, sku = '') => {
    try {
      const response = await axios.post('https://gokulnair.com/new_project/api/update-payment-status', {
        order_id: orderId,
        payment_status: newStatus,
        sku: sku
      });
      
      if (response.data.message === 'Payment status updated') {
        // Refresh orders to show updated status
        fetchOrders();
      }
    } catch (err) {
      console.error('Error updating payment status:', err);
      alert('Failed to update payment status');
    }
  };

  const confirmStatusUpdate = async () => {
    if (pendingStatusUpdate) {
      await updatePaymentStatus(pendingStatusUpdate.orderId, pendingStatusUpdate.newStatus, pendingStatusUpdate.sku);
      setShowConfirmModal(false);
      setPendingStatusUpdate(null);
    }
  };

  const cancelStatusUpdate = () => {
    setShowConfirmModal(false);
    setPendingStatusUpdate(null);
  };

  const getStatusOptions = (currentStatus) => {
    // Admin can only set: 0 (Pending), 2 (Confirmed by Admin), 3 (Rejected by Admin)
    const options = [
      { value: 0, label: 'Pending' },
      { value: 2, label: 'Confirmed by Admin' },
      { value: 3, label: 'Rejected by Admin' }
    ];
    
    return options.map(option => ({
      ...option,
      selected: option.value === currentStatus
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading orders...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard-content">
        {error && <div className="error-message">{error}</div>}
        
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-content">
              <h3>Total Orders</h3>
              <p className="stat-number">{orders.length.toLocaleString()}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <h3>Total Customers</h3>
              <p className="stat-number">{orders.length.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="search-section">
          <div className="search-container">
            <div className="search-icon">🔍</div>
            <input
              type="text"
              placeholder="Search orders by ID, name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="clear-search"
              >
                ✕
              </button>
            )}
          </div>
          <div className="search-results">
            {searchTerm && (
              <p>Found {filteredOrders.length} order(s) matching "{searchTerm}"</p>
            )}
          </div>
        </div>

        <div className="recent-orders">
          <h2>Recent Orders</h2>
          <div className="orders-table">
            {filteredOrders.length === 0 ? (
              <div className="no-orders">
                {searchTerm ? `No orders found matching "${searchTerm}"` : 'No orders found'}
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Payment Type</th>
                    <th>Payment Status</th>
                    <th>Actions</th>
                    <th>Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const isConfirmedByAdmin = order.payment_status === 2;
                    return (
                      <tr key={order.id} className={isConfirmedByAdmin ? 'confirmed-row' : ''}>
                        <td>
                          {isConfirmedByAdmin ? (
                            <span className="order-id-disabled">{order.order_id}</span>
                          ) : (
                            <button 
                              className="order-link"
                              onClick={() => navigate(`/order/${order.order_id}`)}
                            >
                              {order.order_id}
                            </button>
                          )}
                        </td>
                        <td>{order.name}</td>
                        <td>{order.email}</td>
                        <td>{order.phone}</td>
                        <td>
                          <span className="payment-type-badge">
                            {getPaymentTypeText(order.payment_type)}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${getPaymentStatusClass(order.payment_status)}`}>
                            {getPaymentStatusText(order.payment_status)}
                          </span>
                        </td>
                        <td>
                          <div className="status-actions">
                            <select 
                              className="status-dropdown"
                              value={order.payment_status}
                              onChange={(e) => handleStatusUpdate(order.order_id, parseInt(e.target.value), order.sku || '')}
                            >
                              {getStatusOptions(order.payment_status).map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td>{new Date(order.created_at).toLocaleDateString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Status Update</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to change the payment status to "Confirmed by Admin"?</p>
              <p><strong>Order ID:</strong> {pendingStatusUpdate?.orderId}</p>
              {pendingStatusUpdate?.sku && (
                <p><strong>SKU:</strong> {pendingStatusUpdate.sku}</p>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-cancel"
                onClick={cancelStatusUpdate}
              >
                Cancel
              </button>
              <button 
                className="modal-btn modal-btn-confirm"
                onClick={confirmStatusUpdate}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Dashboard; 